import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api";
import { Icon } from "../lib/Icon";
import { ErrorNote, Loading, Page } from "./common";

const ROLE_COLOR: Record<string, string> = {
  admin: "#006747",
  agent: "#2a6f8a",
  security: "#c0612f",
  pm: "#7a5aa8",
};

export function Permissions() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({ queryKey: ["permissions"], queryFn: api.permissions });
  const toggle = useMutation({
    mutationFn: (v: { roleId: string; capability: string; allowed: boolean }) => api.togglePermission(v.roleId, v.capability, v.allowed),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["permissions"] }),
  });

  // Group capabilities by area, preserving catalog order.
  const areas = useMemo(() => {
    const map = new Map<string, { key: string; label: string }[]>();
    for (const cap of data?.capabilities ?? []) {
      const arr = map.get(cap.area) ?? [];
      arr.push({ key: cap.key, label: cap.label });
      map.set(cap.area, arr);
    }
    return [...map.entries()];
  }, [data]);

  if (isLoading) return <Loading />;
  if (error || !data) return <ErrorNote error={error} />;

  const roles = data.roles;
  const allowedSets = new Map(roles.map((r) => [r.id, new Set(r.allowed)] as const));

  return (
    <Page>
      <div>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600, letterSpacing: "-0.6px" }}>Permissions</h1>
        <div style={{ fontSize: 14, color: "#8a8e88", marginTop: 3 }}>
          What each role can do. Every staff member is assigned one role; capabilities are checked at the API.{" "}
          {data.canEdit
            ? "Click any cell to grant or revoke."
            : "Read-only matrix — editing requires the “Edit permissions” capability."}
        </div>
      </div>

      {toggle.isError && (
        <div style={{ padding: "8px 14px", fontSize: 12, color: "#b3261e", background: "#fbe9e7", border: "1px solid #f3d3ce", borderRadius: 6 }}>
          {toggle.error instanceof Error ? toggle.error.message : "Couldn't update"}
        </div>
      )}

      <div style={{ background: "#fff", borderRadius: 3, boxShadow: "inset 0 0 0 1px rgba(0,0,0,.07)", overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <div style={{ minWidth: 620 }}>
            {/* Header row: roles */}
            <div style={{ display: "grid", gridTemplateColumns: `minmax(220px,1fr) repeat(${roles.length}, minmax(96px,140px))`, borderBottom: "1px solid #ececec" }}>
              <div style={{ padding: "12px 18px", fontSize: 10.5, fontWeight: 600, textTransform: "uppercase", color: "#a8aca6" }}>Capability</div>
              {roles.map((r) => (
                <div key={r.id} style={{ padding: "12px 10px", textAlign: "center" }}>
                  <div style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5, fontWeight: 600 }}>
                    <span style={{ width: 7, height: 7, borderRadius: "50%", background: ROLE_COLOR[r.slug] ?? "#8a8e88" }} />
                    {r.name}
                  </div>
                  <div style={{ fontSize: 10.5, color: "#a8aca6", marginTop: 2, fontFamily: "'Space Mono',monospace" }}>
                    {r.staff} {r.staff === 1 ? "person" : "people"}
                  </div>
                </div>
              ))}
            </div>

            {/* Capability rows, grouped by area */}
            {areas.map(([area, caps]) => (
              <div key={area} style={{ display: "contents" }}>
                <div style={{ display: "grid", gridTemplateColumns: `minmax(220px,1fr) repeat(${roles.length}, minmax(96px,140px))`, background: "#faf9f6" }}>
                  <div style={{ padding: "6px 18px", fontSize: 10.5, fontWeight: 700, letterSpacing: ".5px", textTransform: "uppercase", color: "#8a8e88", gridColumn: `1 / span ${roles.length + 1}`, borderTop: "1px solid #f0efe9" }}>
                    {area}
                  </div>
                </div>
                {caps.map((cap) => (
                  <div key={cap.key} style={{ display: "grid", gridTemplateColumns: `minmax(220px,1fr) repeat(${roles.length}, minmax(96px,140px))`, borderTop: "1px solid #f4f3ee" }}>
                    <div style={{ padding: "9px 18px", fontSize: 12.5 }}>{cap.label}</div>
                    {roles.map((r) => {
                      const on = allowedSets.get(r.id)?.has(cap.key) ?? false;
                      const mark = on ? (
                        <Icon name="check" size={15} color={ROLE_COLOR[r.slug] ?? "#006747"} />
                      ) : (
                        <span style={{ width: 6, height: 2, background: "#dcdbd4", borderRadius: 2 }} />
                      );
                      // Writes stay serialized — the whole grid is disabled while any
                      // toggle is in flight, so a second write can't start (the API
                      // guards the last permissions.manage grant, and concurrent writes
                      // would race it). But only the in-flight cell greys, so there's no
                      // full-grid flash. `variables` (latest call) is used for the
                      // cosmetic dim only, never to gate `disabled`.
                      const cellPending = toggle.isPending && toggle.variables?.roleId === r.id && toggle.variables?.capability === cap.key;
                      return (
                        <div key={r.id} style={{ padding: "9px 10px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          {data.canEdit ? (
                            <button
                              onClick={() => toggle.mutate({ roleId: r.id, capability: cap.key, allowed: !on })}
                              disabled={toggle.isPending}
                              title={on ? "Revoke" : "Grant"}
                              aria-label={`${on ? "Revoke" : "Grant"} ${cap.label} for ${r.name}`}
                              style={{ background: on ? "#eef4f0" : "none", border: 0, borderRadius: 6, padding: "5px 10px", cursor: toggle.isPending ? "default" : "pointer", display: "inline-flex", opacity: cellPending ? 0.5 : 1 }}
                            >
                              {mark}
                            </button>
                          ) : (
                            mark
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </Page>
  );
}
